import React, { useEffect, useState } from 'react';
import colors from 'theme/_colors.scss';
import { translate } from 'helpers/translateHelper';
import { isUtcTimestamp } from 'helpers/dateHelper';
import { CHART_PLUGINS } from 'sharedComponents/Chart/constants';
import { convertDailyAgingStressData } from './helpers';
import { renderToStaticMarkup, renderToString } from 'react-dom/server';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import DriveStatusChartToolTipComponent from './DriveStatusChartToolTipComponent';
import { map } from 'lodash';
import { CHART_CONDITION_TREND_KPI_TYPE, CONDITION_TREND_KPI_OPERATIONAL_EVENTS, CONDITION_TREND_KPI_OTHER_EVENTS, CONDITION_TREND_KPI_PROTECTION_EVENTS, statusColumns, TOOLTIP_HIDE_EVENTS } from './constants';
import { statusBarColors } from '../Trend/constants';
import EventsCustomTooltip from '../../EventsCustomTooltip';
import { getXAxisLabelDateFormat } from '../../../helpersV2';

// eslint-disable-next-line import/no-unused-modules
export const chartPlugins = [CHART_PLUGINS.HIGHCHARTS_MORE, CHART_PLUGINS.BOOST, CHART_PLUGINS.NO_DATA_TO_DISPLAY, CHART_PLUGINS.EXPORTING, CHART_PLUGINS.OFFLINE_EXPORTING];

const getPrintableTitle = (assetDetails, chartTitle) => {
    return renderToStaticMarkup(
        <div className='asset-title-chart' id='drive-status-chart-title'>
            <span>{`${assetDetails?.details?.assetName} - ${chartTitle}`}</span>
        </div>
    );
};

const getChartConfig = (assetDetails, title, chartData) => {
    const PRINT_FONT_FAMILY = 'ABBvoice-Regular';

    return {
        resizable: {
            enabled: true,
        },
        chart: {
            zoomType: 'x',
            height: 290,
            type: 'column',
            plotBackgroundColor: null,
            plotShadow: false,
            spacingTop: 70,
            spacingLeft: chartData?.length === 1 ? -17 : -40,
            marginRight: 35,
            events: {
                beforePrint: function () {
                    const printableTitle = getPrintableTitle(assetDetails, title);
                    this.exportSVGElements?.[0]?.hide();
                    this.setTitle({
                        text: printableTitle,
                        useHTML: true,
                        align: 'left',
                        x: 45,
                        y: -50,
                    }, null, false);

                    // Use direct CSS updates instead of this.update() to avoid full re-render
                    const legendElement = this.legend?.group?.element;
                    if (this.xAxis?.length > 0) {
                        const xAxisLabels = this.xAxis[0]?.labelGroup?.element;
                        if (xAxisLabels?.style) {
                            xAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    }

                    this.yAxis?.forEach(axis => {
                        const yAxisLabels = axis.labelGroup?.element;
                        if (yAxisLabels?.style) {
                            yAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    });

                    if (legendElement?.style) {
                        legendElement.style.fontFamily = PRINT_FONT_FAMILY;
                    }
                },
                afterPrint: function () {
                    const chart = this;
                    if (chart.exportSVGElements?.length > 0) {
                        chart.exportSVGElements[0]?.show();
                    }
                    chart.setTitle({
                        text: '',
                    }, null, false);

                    // Use requestAnimationFrame to redraw after browser restores screen layout
                    requestAnimationFrame(() => {
                        chart.redraw();
                    }, 100);
                },
                load: function () {
                    const chart = this;
                    const hideTooltip = () => {
                        if (chart?.pointer) {
                            chart.pointer.reset();
                        }
                    };
                    TOOLTIP_HIDE_EVENTS.forEach(event =>
                        window.addEventListener(event, hideTooltip, true)
                    );
                    chart._hideTooltipOnScroll = hideTooltip;
                },
                destroy: function () {
                    const fn = this._hideTooltipOnScroll;
                    if (fn) {
                        TOOLTIP_HIDE_EVENTS.forEach(event =>
                            window.removeEventListener(event, fn, true)
                        );
                        delete this._hideTooltipOnScroll;
                    }
                }
            },
        },
        boost: {
            allowForce: false //Workaround for highchart bug. Allows correctly disabling boost, when zooming in.
        },
        title: {
            text: '',
        },
        time: {
            useUTC: false
        },
        xAxis: {
            type: 'datetime',
            scrollbar: {
                enabled: false,
            },
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
                day: '%e. %b',
                month: '%b \'%y'
            },
            labels: {
                rotation: 50,
                color: '#000000',
                style: {
                    fontWeight: 'bold',
                    fontColor: '#000000'
                }
            },
            id: 'xAxis',
            lineColor: '#000000',
            lineWidth: 2,
            tickPixelInterval: 25,
            tickColor: '#000000',
            tickWidth: 2,
            // Add these properties to start on exact data point
            startOnTick: false,
            endOnTick: false,
            minPadding: 0,
            maxPadding: 0,
        },
        plotOptions: {
            series: {
                pointPadding: 0,
                groupPadding: 0,
                borderWidth: 0,
                shadow: false,
                turboThreshold: 0,
                animation: false,
                boostThreshold: 500,
            },
            column: {
                groupPadding: 0,
                pointPadding: 0,
                borderWidth: 0,
                lineWidth: 1,
                states: {
                    hover: {
                        borderColor: '#000000',
                        borderWidth: 2,
                        brightness: 0
                    }
                }
            }
        },
        lang: {
            noData: renderToStaticMarkup(<ChartNoDataText
                title={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataDailyAgingStress')}
                text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuideDailyAgingStress')} />),
            printChart: translate('ABB.Powertrain.Frontend.printChart'),
            downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
        },
        noData: {
            useHTML: true
        },
        tooltip: {
            useHTML: true,
            outside: true,
            backgroundColor: 'transparent',
            borderWidth: 0,
            shadow: false,
            stickyTracking: false,
            hideDelay: 0,
            formatter: function () {
                if (this.point?.series?.name === CHART_CONDITION_TREND_KPI_TYPE) {
                    return renderToString(
                        <DriveStatusChartToolTipComponent
                            timeStamp={this.point?.options?.timestamp}
                            value={this.point?.options}
                            isUTC={false}
                            showTimePortion={isUtcTimestamp(this.point?.options?.timestamp)}
                        />
                    );
                }
                return renderToString(
                    <EventsCustomTooltip
                        timestamp={this.point?.options?.x}
                        events={this.point?.options?.events}
                        condition={this.point?.options?.condition}
                        showTimePortion={isUtcTimestamp(this.point?.options?.timestamp)}
                    />
                );
            },
        },
        legend: { enabled: false },
        credits: {
            enabled: false
        },
        navigation: {
            theme: {
                states: {
                    hover: {
                        fill: colors.white,
                        stroke: 'none'
                    },
                    select: {
                        fill: colors.white,
                        select: 'none'
                    }
                }
            },
            buttonOptions: {
                align: 'right'
            },
            menuStyle: {
                borderColor: '#fff',
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '6px'
            },
            menuItemStyle: {
                padding: '6px 12px',
                fontSize: '14px',
                fontFamily: 'ABBvoice-Regular',
            },
            menuItemHoverStyle: {
                backgroundColor: '#ebebeb',
                color: '#1f1f1f'
            }
        },
        exporting: {
            enabled: true,
            sourceWidth: 1600,
            sourceHeight: chartData?.length === 1 ? 185 : 320,
            scale: 1,
            fallbackToExportServer: false,
            allowHTML: true,
            buttons: {
                contextButton: {
                    menuItems: ['downloadPNG', 'printChart'],
                    verticalAlign: 'top',
                    x: 0,
                    y: -70,
                    zIndex: 0
                }
            },
            chartOptions: {
                chart: {
                    height: chartData?.length === 1 ? 185 : 320,
                    marginRight: 35,
                },
                title: {
                    text: `${assetDetails?.details?.assetName} - ${title}`,
                    align: 'left',
                    x: 50,
                    y: -20,
                    style: {
                        fontFamily: 'Verdana, sans-serif',
                        fontWeight: 'bold'
                    }
                },
                xAxis: {
                    labels: {
                        style: {
                            fontSize: '10px',
                        }
                    }
                },
                yAxis: {
                    labels: {
                        style: {
                            fontFamily: 'Verdana, sans-serif',
                        }
                    }
                },
                lang: {
                    noData: renderToStaticMarkup(
                        <div style={{
                            display: 'block',
                            width: '100%',
                            maxWidth: '1200px',
                            textAlign: 'center',
                            padding: '20px 60px',
                            boxSizing: 'border-box',
                        }}>
                            <strong style={{
                                display: 'block',
                                maxWidth: '520px',
                                marginBottom: '26px',
                                padding: '10px 18px',
                                boxSizing: 'border-box',
                                fontSize: '14px',
                                fontFamily: 'Verdana, sans-serif',
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}>{translate('ABB.Powertrain.Frontend.chartNoMeasurementDataDailyAgingStress')}</strong>

                            <span style={{
                                display: 'block',
                                maxWidth: '900px',
                                marginBottom: '100px',
                                fontSize: '12px',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal',
                                lineHeight: '1.5',
                                fontFamily: 'Verdana, sans-serif',
                                textAlign: 'center'
                            }}>{translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuideDailyAgingStress')}</span>
                        </div>
                    ),
                },
                noData: {
                    useHTML: true
                }
            }
        }
    };
};

const getStatusBarData = (trendValues, kpiType) => {
    const getKpiTypeTrend = trendValues?.find(trend => trend.type === kpiType);
    if (getKpiTypeTrend) {
        return map(getKpiTypeTrend?.values, (ul) => {
            return {
                y: 1, // Able to adjust status bar height by using y axis value
                x: new Date(ul?.timestamp)?.getTime(),
                color: statusBarColors[ul?.value],
                events: ul?.events ?? [],
                condition: ul?.value,
                label: statusColumns[ul?.value]?.label,
                timestamp: ul?.timestamp,
                isFromConditionTrendsHourly: !!getKpiTypeTrend?.isFromConditionTrendsHourly,
            };
        }).filter(data => data.condition !== null);
    }
    return [];
};

const getSeries = (chartData, isProtectionEventVisible, isOperationalEventVisible, isOtherEventVisible) => {
    const seriesConfig = { yAxis: [], seriesData: [] };
    const yAxisId = 'yaxis';
    const driveEventsYAxisId = 'drive_events_yaxis';
    const protectionEventsYAxisId = 'protection_events_yaxis';
    const operationalEventsYAxisId = 'operational_events_yaxis';
    const otherEventsYAxisId = 'other_events_yaxis';

    if (chartData?.length) {

        // Y axis configuration
        seriesConfig.yAxis.push({
            top: '0%',
            height: '19px',
            offset: 0,
            id: yAxisId,
            title: {
                text: translate('ABB.Powertrain.Frontend.conditionDriveChartTitle'),
                align: 'low',
                x: 10,
                y: -20,
                style: {
                    fontFamily: 'ABBvoice-Bold',
                    fontWeight: 700,
                    fontSize: '10px',
                    lineHeight: '12px',
                    whiteSpace: 'nowrap',
                    transform: 'rotate(0deg)',
                    color: '#1F1F1F'
                },
            },
            labels: { enabled: false },
            gridLineWidth: 0,
            lineWidth: 0,
            tickLength: 0,
        });
        if (isProtectionEventVisible || isOperationalEventVisible || isOtherEventVisible) {
            // Drive events sub header
            seriesConfig.yAxis.push({
                id: driveEventsYAxisId,
                height: '8px',
                top: '35%',
                title: {
                    text: translate('ABB.Powertrain.Frontend.driveEventsTitle'),
                    align: 'low',
                    x: 33,
                    y: -20,
                    style: {
                        fontFamily: 'ABBvoice-Bold',
                        fontWeight: 700,
                        fontSize: '10px',
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(0deg)',
                        color: '#1F1F1F'
                    },
                },
                labels: { enabled: false },
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
            });
        }
        if (isProtectionEventVisible) {
            seriesConfig.yAxis.push({
                top: '39%',
                height: '19px',
                offset: 0,
                id: protectionEventsYAxisId,
                title: {
                    text: translate('ABB.Powertrain.Frontend.protectionEventsLabel'),
                    align: 'low',
                    x: 10,
                    y: -20,
                    style: {
                        fontFamily: 'ABBvoice-Regular',
                        fontWeight: 400,
                        fontSize: '10px',
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(0deg)',
                        color: '#1F1F1F'
                    },
                },
                labels: { enabled: false },
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
            });
        }
        if (isOperationalEventVisible) {
            seriesConfig.yAxis.push({
                top: '63%',
                height: '19px',
                offset: 0,
                id: operationalEventsYAxisId,
                title: {
                    text: translate('ABB.Powertrain.Frontend.operationalEventsLabel'),
                    align: 'low',
                    x: 10,
                    y: -20,
                    style: {
                        fontFamily: 'ABBvoice-Regular',
                        fontWeight: 400,
                        fontSize: '10px',
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(0deg)',
                        color: '#1F1F1F'
                    },
                },
                labels: { enabled: false },
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
            });
        }
        if (isOtherEventVisible) {
            seriesConfig.yAxis.push({
                top: '87%',
                height: '19px',
                offset: 0,
                id: otherEventsYAxisId,
                title: {
                    text: translate('ABB.Powertrain.Frontend.otherEventsLabel'),
                    align: 'low',
                    x: 10,
                    y: -20,
                    style: {
                        fontFamily: 'ABBvoice-Regular',
                        fontWeight: 400,
                        fontSize: '10px',
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                        transform: 'rotate(0deg)',
                        color: '#1F1F1F'
                    },
                },
                labels: { enabled: false },
                gridLineWidth: 0,
                lineWidth: 0,
                tickLength: 0,
            });
        }

        // Bar chart series config
        seriesConfig.seriesData.push({
            name: CHART_CONDITION_TREND_KPI_TYPE,
            type: 'column',
            id: `${crypto.randomUUID()}`,
            yAxis: yAxisId,
            xAxis: 'xAxis',
            data: getStatusBarData(chartData, CHART_CONDITION_TREND_KPI_TYPE),
        });
        if (isProtectionEventVisible) {
            seriesConfig.seriesData.push({
                name: CONDITION_TREND_KPI_PROTECTION_EVENTS,
                type: 'column',
                id: `${crypto.randomUUID()}`,
                yAxis: protectionEventsYAxisId,
                xAxis: 'xAxis',
                data: getStatusBarData(chartData, CONDITION_TREND_KPI_PROTECTION_EVENTS),
            });
        }
        if (isOperationalEventVisible) {
            seriesConfig.seriesData.push({
                name: CONDITION_TREND_KPI_OPERATIONAL_EVENTS,
                type: 'column',
                id: `${crypto.randomUUID()}`,
                yAxis: operationalEventsYAxisId,
                xAxis: 'xAxis',
                data: getStatusBarData(chartData, CONDITION_TREND_KPI_OPERATIONAL_EVENTS),
            });
        }
        if (isOtherEventVisible) {
            seriesConfig.seriesData.push({
                name: CONDITION_TREND_KPI_OTHER_EVENTS,
                type: 'column',
                id: `${crypto.randomUUID()}`,
                yAxis: otherEventsYAxisId,
                xAxis: 'xAxis',
                data: getStatusBarData(chartData, CONDITION_TREND_KPI_OTHER_EVENTS),
            });
        }
    }

    return { ...seriesConfig };
};

const useChartConfig = (chartData, title, assetDetails) => {
    const [options, setOptions] = useState({});
    const chartConfig = getChartConfig(assetDetails, title, chartData);
    const isProtectionEventVisible = chartData?.some(kpi => kpi.type === CONDITION_TREND_KPI_PROTECTION_EVENTS);
    const isOperationalEventVisible = chartData?.some(kpi => kpi.type === CONDITION_TREND_KPI_OPERATIONAL_EVENTS);
    const isOtherEventVisible = chartData?.some(kpi => kpi.type === CONDITION_TREND_KPI_OTHER_EVENTS);
    useEffect(() => {
        const { xUnit } = convertDailyAgingStressData(chartData);
        const { yAxis, seriesData } = getSeries(chartData, isProtectionEventVisible, isOperationalEventVisible, isOtherEventVisible);
        chartConfig.yAxis = yAxis;
        chartConfig.series = seriesData;
        chartConfig.enableNoDataZoom = true;
        chartConfig.xAxis.title = {
            text: xUnit,
        };
        chartConfig.xAxis.labels.formatter = function () {
            return getXAxisLabelDateFormat(this.value);
        };
        if (chartData?.length === 1) {
            chartConfig.chart.height = 155;
        }
        if (chartConfig.series && chartConfig.series.every(s => !s.data || s.data.length === 0)) {
            if (Array.isArray(chartConfig.yAxis)) {
                chartConfig.yAxis = chartConfig.yAxis.map(axis => ({ ...axis, visible: false }));
            }
            if (chartConfig.xAxis) {
                chartConfig.xAxis.visible = false;
            }
        } else {
            // Always show axes if there is data
            if (Array.isArray(chartConfig.yAxis)) {
                chartConfig.yAxis = chartConfig.yAxis.map(axis => ({ ...axis, visible: true }));
            }
            if (chartConfig.xAxis) {
                chartConfig.xAxis.visible = true;
            }
        }
        setOptions({ ...chartConfig });
    }, [chartData]);

    return options;
};

export {
    getChartConfig,
    getStatusBarData,
    getSeries
};

export default useChartConfig;
